import { Tooltip } from "@mui/material";
import { JobCodeForm } from "../../../../forms/form-components/job/update/job-code.form";
import { JobDocumentEntityType } from "@/lib/supabase/supabaseTypes";
import { JobTitleForm } from "../../../../forms/form-components/job/update/job-title.form";

import {
	MessageCircle,
	QrCode,
	FileText,
	PanelLeftOpen,
	PanelLeftClose,
	Loader2,
} from "lucide-react";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { PinJob } from "../../pin-job.component";
import { JobStatusEnum, jobStatusToLabel } from "@/lib/types/job";
import { Button as ShadcnButton } from "@/dentlab/src/components/ui/button";
import { FileViewerModal } from "@/components/src/file-viewer";
import { useJobPrintActions } from "./use-job-print-actions.hook";
import { JobStatusSelect } from "@/dentlab/src/forms/form-input-components/selects/job-status.select";
import { getStatusIcon } from "@/dentlab/src/lib/utils/job-utils";

export const isPatientOrPractitionerEditable = (
	jobDocuments: JobDocumentEntityType[]
) => {
	if (jobDocuments.length === 0) {
		return true;
	}
	return !jobDocuments.some(
		(jobDocument) => jobDocument.status === JobStatusEnum.COMPLETED
	);
};

export const JobPageHeader: React.FC<{
	chatOpen: boolean;
	onToggleChat: () => void;
	isJobListOpen?: boolean;
	onToggleJobList?: () => void;
}> = ({ chatOpen, onToggleChat, isJobListOpen, onToggleJobList }) => {
	const { job } = useJobStore((state) => ({
		job: state.job,
	}));

	const {
		pdfViewer,
		closePdfViewer,
		loadingAuftragsblatt,
		loadingEtikett,
		handleCreateAuftragsblatt,
		handleCreateEtikett,
	} = useJobPrintActions();

	return (
		<div className="h-full bg-white">
			<div className="flex justify-between items-center border-b border-gray-200 h-10">
				<div>
					<div className="flex items-center ml-2 gap-2">
						{onToggleJobList && (
							<Tooltip
								title={
									isJobListOpen
										? "Auftragsliste schließen"
										: "Auftragsliste öffnen"
								}
							>
								<ShadcnButton
									variant="ghost"
									size="icon"
									onClick={onToggleJobList}
									className="w-8 h-8"
								>
									{isJobListOpen ? (
										<PanelLeftClose className="w-5 h-5" />
									) : (
										<PanelLeftOpen className="w-5 h-5" />
									)}
								</ShadcnButton>
							</Tooltip>
						)}

						<div className="flex items-center gap-2">
							<JobTitleForm />
							<JobCodeForm
								jobId={job?.id ?? 0}
								jobCode={job?.code ?? ""}
								jobStatus={job?.status as JobStatusEnum}
							/>
							<div>
								{job?.status && (
									<span className="text-md text-black-500 flex items-center gap-2 font-medium bg-gray-100 px-2 py-1 rounded-md">
									{getStatusIcon(job?.status as JobStatusEnum)} {" "} {jobStatusToLabel[job?.status as JobStatusEnum]}</span>
								)}
							</div>
						</div>
					</div>
				</div>
				<div className="flex items-center">
					<div className="flex items-center mr-2">
						<Tooltip title="Auftragsblatt drucken">
							<ShadcnButton
								variant="ghost"
								size="icon"
								onClick={handleCreateAuftragsblatt}
								className="w-8 h-8"
								disabled={loadingAuftragsblatt}
								aria-label="Auftragsblatt drucken"
								data-testid="print-auftragsblatt"
							>
								{loadingAuftragsblatt ? (
									<Loader2 className="w-5 h-5 animate-spin" />
								) : (
									<FileText className="w-5 h-5" />
								)}
							</ShadcnButton>
						</Tooltip>
						<Tooltip title="Auftragsetikett drucken">
							<ShadcnButton
								variant="ghost"
								size="icon"
								onClick={handleCreateEtikett}
								className="w-8 h-8"
								disabled={loadingEtikett}
								aria-label="Auftragsetikett drucken"
								data-testid="print-label-auftrag"
							>
								{loadingEtikett ? (
									<Loader2 className="w-5 h-5 animate-spin" />
								) : (
									<QrCode className="w-5 h-5" />
								)}
							</ShadcnButton>
						</Tooltip>
						{job && <PinJob job={job} />}
						{!chatOpen && (
							<ShadcnButton
								variant="ghost"
								size="sm"
								onClick={onToggleChat}
								className="h-8 gap-1.5"
								aria-label="Chat öffnen"
								data-testid="toggle-chat"
							>
								<MessageCircle className="w-4 h-4" />
								<span className="text-xs">Chat</span>
							</ShadcnButton>
						)}
					</div>
				</div>
			</div>
			{pdfViewer && (
				<FileViewerModal
					fileIds={[]}
					isOpen={true}
					onClose={closePdfViewer}
					blobUrl={pdfViewer.url}
					blobFileName={pdfViewer.fileName}
				/>
			)}
		</div>
	);
};
